package org.example.project.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.example.project.CheckTaxInfoResult
import org.example.project.FirebaseManager
import org.example.project.UserCheckResult
import org.example.project.OrgProvisionResult
import org.example.project.SystemProfileDocument
import org.example.project.UserRoleType
import org.example.project.PrimaryColor

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProvisionLicensesSection(
    currentSystemProfile: SystemProfileDocument?,
    taxCountriesList: List<String>,
    fiatPaymentMethodsList: List<String>
) {
    val scope = rememberCoroutineScope()
    val isAdmin = currentSystemProfile?.role == UserRoleType.ADMIN

    var recipientEmail by remember { mutableStateOf("") }
    var provisionAmount by remember { mutableStateOf("") }
    var isProvisioning by remember { mutableStateOf(false) }
    var provisionMessage by remember { mutableStateOf<String?>(null) }
    var showProvisionConfirm by remember { mutableStateOf(false) }
    var targetUidTail by remember { mutableStateOf("") }
    var targetRole by remember { mutableStateOf(UserRoleType.USER) }

    // Admin extra settle info for provisioning
    var settleFiatMethod by remember { mutableStateOf("") }
    var settleFiatProof by remember { mutableStateOf("") }
    var settleFiatAmount by remember { mutableStateOf("") }
    var settleTaxCountry by remember { mutableStateOf("") }
    var settleTaxId by remember { mutableStateOf("") }
    var settleTaxRate by remember { mutableStateOf("") }
    var settleRemark by remember { mutableStateOf("") }
    var isFetchingTaxForProvision by remember { mutableStateOf(false) }

    var settleFiatMethodExpanded by remember { mutableStateOf(false) }
    var settleTaxCountryExpanded by remember { mutableStateOf(false) }

    val tfColors = OutlinedTextFieldDefaults.colors(
        focusedBorderColor = PrimaryColor,
        unfocusedBorderColor = Color(0xFFE2E8F0)
    )

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Text(
                text = "Provision Licenses",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF64748B) // Slate 500
            )
            Spacer(modifier = Modifier.height(16.dp))
            
            OutlinedTextField(
                value = recipientEmail,
                onValueChange = { recipientEmail = it },
                label = { Text("Target Member Email") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = tfColors
            )
            
            Spacer(modifier = Modifier.height(12.dp))
            
            OutlinedTextField(
                value = provisionAmount,
                onValueChange = { provisionAmount = it.filter { char -> char.isDigit() } },
                label = { Text("License Allocation Amount") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = tfColors
            )

            if (isAdmin) {
                Spacer(modifier = Modifier.height(20.dp))
                HorizontalDivider(color = Color(0xFFF1F5F9))
                Spacer(modifier = Modifier.height(16.dp))
                
                Text("Admin: Offline B2B Payment Details", style = MaterialTheme.typography.labelLarge, color = Color(0xFF64748B))
                Spacer(modifier = Modifier.height(12.dp))
                
                OutlinedButton(
                    onClick = {
                        if (recipientEmail.isNotBlank()) {
                            scope.launch {
                                isFetchingTaxForProvision = true
                                val result = FirebaseManager.checkTaxInfo(recipientEmail)
                                if (result is CheckTaxInfoResult.Success) {
                                    settleTaxCountry = result.data.tax_country ?: ""
                                    settleTaxId = result.data.tax_id ?: ""
                                } else if (result is CheckTaxInfoResult.Error) {
                                    provisionMessage = "Error: Failed to load billing info - ${result.message}"
                                }
                                isFetchingTaxForProvision = false
                            }
                        }
                    },
                    enabled = recipientEmail.isNotBlank() && !isFetchingTaxForProvision,
                    modifier = Modifier.fillMaxWidth(),
                    shape = MaterialTheme.shapes.small,
                    border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = Color(0xFF64748B))
                ) {
                    if (isFetchingTaxForProvision) {
                        CircularProgressIndicator(modifier = Modifier.size(16.dp), color = Color(0xFF64748B), strokeWidth = 2.dp)
                    } else {
                        Text("Auto-fill Partner Billing Info", fontSize = 14.sp)
                    }
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                ExposedDropdownMenuBox(
                    expanded = settleFiatMethodExpanded,
                    onExpandedChange = { settleFiatMethodExpanded = !settleFiatMethodExpanded },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    OutlinedTextField(
                        value = settleFiatMethod,
                        onValueChange = { settleFiatMethod = it },
                        readOnly = false,
                        label = { Text("Payment Method") },
                        modifier = Modifier.fillMaxWidth().menuAnchor(MenuAnchorType.PrimaryEditable, true),
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = settleFiatMethodExpanded) },
                        colors = tfColors,
                        singleLine = true
                    )
                    ExposedDropdownMenu(
                        expanded = settleFiatMethodExpanded,
                        onDismissRequest = { settleFiatMethodExpanded = false }
                    ) {
                        fiatPaymentMethodsList.forEach { method ->
                            DropdownMenuItem(
                                text = { Text(method) },
                                onClick = {
                                    settleFiatMethod = method
                                    settleFiatMethodExpanded = false
                                }
                            )
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                OutlinedTextField(
                    value = settleFiatProof,
                    onValueChange = { settleFiatProof = it },
                    label = { Text("Payment Reference / Invoice") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = tfColors
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                OutlinedTextField(
                    value = settleFiatAmount,
                    onValueChange = { settleFiatAmount = it },
                    label = { Text("Contract Value ($)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = tfColors
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                OutlinedTextField(
                    value = settleTaxRate,
                    onValueChange = { settleTaxRate = it },
                    label = { Text("Tax / VAT Rate (%)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = tfColors
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                ExposedDropdownMenuBox(
                    expanded = settleTaxCountryExpanded,
                    onExpandedChange = { settleTaxCountryExpanded = !settleTaxCountryExpanded },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    OutlinedTextField(
                        value = settleTaxCountry,
                        onValueChange = { settleTaxCountry = it },
                        readOnly = false,
                        label = { Text("Billing Region") },
                        modifier = Modifier.fillMaxWidth().menuAnchor(MenuAnchorType.PrimaryEditable, true),
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = settleTaxCountryExpanded) },
                        colors = tfColors,
                        singleLine = true
                    )
                    ExposedDropdownMenu(
                        expanded = settleTaxCountryExpanded,
                        onDismissRequest = { settleTaxCountryExpanded = false }
                    ) {
                        taxCountriesList.forEach { country ->
                            DropdownMenuItem(
                                text = { Text(country) },
                                onClick = {
                                    settleTaxCountry = country
                                    settleTaxCountryExpanded = false
                                }
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(12.dp))
                OutlinedTextField(
                    value = settleTaxId,
                    onValueChange = { settleTaxId = it },
                    label = { Text("Tax / VAT ID") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = tfColors
                )
                Spacer(modifier = Modifier.height(12.dp))
                OutlinedTextField(
                    value = settleRemark,
                    onValueChange = { settleRemark = it },
                    label = { Text("Internal Notes") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = tfColors
                )
            }
            
            Spacer(modifier = Modifier.height(20.dp))
            
            Button(
                onClick = {
                    val amount = provisionAmount.toIntOrNull() ?: 0
                    if (recipientEmail.isNotBlank() && amount > 0) {
                        scope.launch {
                            isProvisioning = true
                            provisionMessage = null
                            val checkResult = FirebaseManager.userCheck(recipientEmail)
                            when (checkResult) {
                                is UserCheckResult.Success -> {
                                    targetUidTail = checkResult.data.uid_tail
                                    targetRole = checkResult.data.role
                                    
                                    // If Admin is transferring, try to pre-fill tax info
                                    if (isAdmin && targetRole != UserRoleType.ADMIN) {
                                        val taxResult = FirebaseManager.checkTaxInfo(recipientEmail)
                                        if (taxResult is CheckTaxInfoResult.Success) {
                                            settleTaxCountry = taxResult.data.tax_country ?: ""
                                            settleTaxId = taxResult.data.tax_id ?: ""
                                        }
                                    }
                                    
                                    showProvisionConfirm = true
                                }
                                is UserCheckResult.Error -> {
                                    provisionMessage = "Error: ${checkResult.message}"
                                }
                            }
                            isProvisioning = false
                        }
                    }
                },
                enabled = !isProvisioning && recipientEmail.isNotBlank() && (provisionAmount.toIntOrNull() ?: 0) > 0,
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.medium,
                colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor)
            ) {
                if (isProvisioning) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text("Provision Licenses", fontWeight = FontWeight.Bold)
                }
            }

            if (provisionMessage != null) {
                val isError = provisionMessage!!.startsWith("Error")
                Spacer(modifier = Modifier.height(16.dp))
                Surface(
                    color = if (isError) Color(0xFFFEF2F2) else Color(0xFFF0FDF4),
                    shape = MaterialTheme.shapes.small,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = provisionMessage!!,
                            color = if (isError) Color(0xFFDC2626) else Color(0xFF16A34A),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
        }
    }

    if (showProvisionConfirm) {
        val currentRole = currentSystemProfile?.role ?: UserRoleType.USER
        val canTransfer = when (currentRole) {
            UserRoleType.ADMIN -> targetRole != UserRoleType.ADMIN
            UserRoleType.ORGANIZATION -> targetRole == UserRoleType.USER
            else -> false
        }

        AlertDialog(
            onDismissRequest = { 
                if (!isProvisioning) showProvisionConfirm = false 
            },
            title = { Text("Confirm Provisioning", fontWeight = FontWeight.Bold) },
            text = { 
                Column {
                    Text("Account: $recipientEmail", fontWeight = FontWeight.Bold, color = Color(0xFF334155))
                    Text("User ID Tail: $targetUidTail", fontWeight = FontWeight.Medium, color = Color(0xFF64748B), style = MaterialTheme.typography.bodySmall)
                    Spacer(modifier = Modifier.height(16.dp))
                    
                    if (canTransfer) {
                        Text("Are you sure you want to provision ${provisionAmount.toIntOrNull() ?: 0} licenses to this user?", color = Color(0xFF334155))
                    } else {
                        Text(
                            text = "Permission Denied: You cannot provision licenses to this user.",
                            color = Color(0xFFDC2626),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val amount = provisionAmount.toIntOrNull() ?: 0
                        if (amount > 0 && canTransfer) {
                            scope.launch {
                                isProvisioning = true
                                val result = FirebaseManager.orgProvision(
                                    targetEmail = recipientEmail, 
                                    amount = amount,
                                    settleFiatMethod = if (isAdmin) settleFiatMethod.takeIf { it.isNotBlank() } else null,
                                    settleFiatProof = if (isAdmin) settleFiatProof.takeIf { it.isNotBlank() } else null,
                                    settleFiatAmount = if (isAdmin) settleFiatAmount.toIntOrNull() else null,
                                    settleTaxCountry = if (isAdmin) settleTaxCountry.takeIf { it.isNotBlank() } else null,
                                    settleTaxId = if (isAdmin) settleTaxId.takeIf { it.isNotBlank() } else null,
                                    settleTaxRate = if (isAdmin) settleTaxRate.toIntOrNull() else null,
                                    settleRemark = if (isAdmin) settleRemark.takeIf { it.isNotBlank() } else null
                                )
                                when (result) {
                                    is OrgProvisionResult.Success -> {
                                        provisionMessage = "Successfully provisioned $amount licenses to $recipientEmail!"
                                        recipientEmail = ""
                                        provisionAmount = ""
                                        settleFiatMethod = ""
                                        settleFiatProof = ""
                                        settleFiatAmount = ""
                                        settleTaxCountry = ""
                                        settleTaxId = ""
                                        settleTaxRate = ""
                                        settleRemark = ""
                                        showProvisionConfirm = false
                                    }
                                    is OrgProvisionResult.Error -> {
                                        provisionMessage = "Error: ${result.message}"
                                        showProvisionConfirm = false
                                    }
                                }
                                isProvisioning = false
                            }
                        }
                    },
                    enabled = !isProvisioning && canTransfer,
                    colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor)
                ) {
                    if (isProvisioning) {
                        CircularProgressIndicator(modifier = Modifier.size(20.dp), color = Color.White, strokeWidth = 2.dp)
                    } else {
                        Text("Confirm")
                    }
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showProvisionConfirm = false },
                    enabled = !isProvisioning
                ) {
                    Text("Cancel", color = Color(0xFF64748B))
                }
            }
        )
    }
}
